/**
 * wsReconnect.ts — PHONE-A4 (V-12). One reconnect policy, shared by every
 * WebSocket hook in the app.
 *
 * WHAT WAS WRONG. `useInboxSocket` and `useNotificationSocket` each carried
 * their own copy of the same six lines: reconnect on a flat 5-second timer,
 * forever, with no backoff, no jitter and no wake-up. Three consequences, all
 * of them visible in production logs:
 *
 *  1. THE HAMMER. When the server rejects the upgrade — an expired session is
 *     the common case — the socket closes immediately, so "every 5 seconds"
 *     becomes a steady 401 storm from every open tab, for as long as the tab
 *     stays open. A browser WebSocket cannot read the response status (the
 *     spec deliberately hides it), so the client CANNOT distinguish "you are
 *     logged out" from "the server is redeploying". Backoff is therefore the
 *     only correct answer: it costs a logged-out tab almost nothing and it
 *     lets a deploying server come back without being flooded.
 *
 *  2. THE THUNDERING HERD. A fixed delay means every client that was connected
 *     when a Railway deploy cycled the server retries in the same 5-second
 *     tick, forever, in lockstep. Jitter breaks the lockstep.
 *
 *  3. THE SLEEPING PHONE. A laptop that sleeps wakes with a dead socket and a
 *     timer that may not fire for up to a minute — and the softphone's ring
 *     banner rides this exact channel, so a call arriving in that window
 *     reached nobody. Waking on `visibilitychange` and `online` reconnects the
 *     instant the machine is usable again, which is the difference between
 *     "the phone works when I open the lid" and "the phone works a minute
 *     later, sometimes".
 *
 * The server-side heartbeat that detects a half-open socket from the OTHER end
 * is a separate fix in wsServer.ts (frozen) and is NOT part of this batch.
 */

export interface WsBackoffOptions {
  /** First retry delay, in ms. Doubles from here. */
  baseMs?: number;
  /** Ceiling for the doubling, in ms. */
  maxMs?: number;
  /**
   * How long a socket must stay OPEN before the backoff is considered to have
   * "worked" and resets to baseMs. Without this, a socket that connects and is
   * dropped again immediately (the server accepting the upgrade and then
   * closing it) would reset the backoff on every cycle and hammer just as hard
   * as the flat timer did.
   */
  resetAfterOpenMs?: number;
  /** Fraction of the delay applied as random jitter (0.25 = ±25%). */
  jitterRatio?: number;
}

const DEFAULTS: Required<WsBackoffOptions> = {
  baseMs: 1000,
  maxMs: 60000,
  resetAfterOpenMs: 10000,
  jitterRatio: 0.25,
};

export interface WsBackoff {
  /** The delay to wait before the NEXT attempt, and advance the schedule. */
  nextDelay(): number;
  /** Call when a socket opens. Records the moment for `noteClosed`. */
  noteOpen(now?: number): void;
  /**
   * Call when a socket closes. If it had been open for at least
   * `resetAfterOpenMs`, the backoff resets to base — a genuinely healthy
   * connection that later drops should retry FAST.
   */
  noteClosed(now?: number): void;
  /** Force the schedule back to base (an explicit wake-up). */
  reset(): void;
  /** The un-jittered delay the next attempt would use. Exposed for tests. */
  peek(): number;
}

export function createWsBackoff(opts: WsBackoffOptions = {}): WsBackoff {
  const { baseMs, maxMs, resetAfterOpenMs, jitterRatio } = { ...DEFAULTS, ...opts };
  let delay = baseMs;
  let openedAt: number | null = null;

  return {
    peek: () => delay,
    reset() {
      delay = baseMs;
      openedAt = null;
    },
    noteOpen(now = Date.now()) {
      openedAt = now;
    },
    noteClosed(now = Date.now()) {
      if (openedAt !== null && now - openedAt >= resetAfterOpenMs) delay = baseMs;
      openedAt = null;
    },
    nextDelay() {
      const current = delay;
      delay = Math.min(delay * 2, maxMs);
      // Symmetric jitter around the scheduled delay. Clamped at >= 0 so a
      // jitterRatio > 1 (nobody should, but) can never produce a negative
      // setTimeout, which browsers treat as 0 and would re-create the hammer.
      const spread = current * jitterRatio;
      return Math.max(0, Math.round(current - spread + Math.random() * spread * 2));
    },
  };
}

/**
 * Fires `onWake` when the machine becomes usable again: the tab is brought to
 * the front, or the OS reports the network is back. Returns an unsubscribe.
 *
 * Deliberately NOT debounced here — the caller decides what waking means. Both
 * socket hooks use it to reconnect immediately if their socket is not open,
 * and to do nothing at all if it is.
 */
export function onNetworkWake(onWake: () => void): () => void {
  if (typeof window === 'undefined') return () => { /* SSR / non-DOM */ };

  const handleVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') onWake();
  };
  const handleOnline = () => onWake();

  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', handleVisibility);
  window.addEventListener('online', handleOnline);

  return () => {
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', handleVisibility);
    window.removeEventListener('online', handleOnline);
  };
}
